import {
    ApplicationConfig,
    EnvironmentProviders,
    provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';
import {
  provideClientHydration,
  withEventReplay,
  withHttpTransferCacheOptions,
  withNoIncrementalHydration
} from '@angular/platform-browser';
import { provideHttpClient } from '@angular/common/http';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';

import { routes } from '@app/app.routes';
import { provideAppLanguage } from '@core/i18n/language';
import { BUILD_VERSION } from '@env/build-version';

/**
 * Router setup. Exported so `app.config.spec.ts` can assert the real configuration the app
 * bootstraps with, rather than a copy of it.
 */
export const routerProviders: EnvironmentProviders = provideRouter(
    routes,
    withComponentInputBinding(),
    // Without a router scroller Angular never touches the scroll offset, so an in-app
    // navigation carries the previous page's offset over. CMS pages fetch their sections
    // after `NavigationEnd`, so the document briefly collapses to header + footer: iOS
    // Safari holds the stale offset (showing the footer), then honours it once the sections
    // render — landing the reader at the bottom of the new page. `enabled` scrolls to the
    // top on a forward navigation and restores the stored offset on back/forward, and it
    // switches `history.scrollRestoration` to `manual` so the browser stops competing.
    // `anchorScrolling` keeps `#fragment` links working (`scroll-padding-top` in
    // `styles.scss` already clears the fixed header).
    withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
    }),
);

export const appConfig: ApplicationConfig = {
    providers: [
        provideBrowserGlobalErrorListeners(),
        routerProviders,
        provideClientHydration(
            withEventReplay(),
            // GraphQL uses POST; cache it so the SSR fetch transfers to the client (no double fetch).
            withHttpTransferCacheOptions({ includePostRequests: true }),
            // v22 enables incremental hydration by default; opt out to retain pre-v22 behavior.
            withNoIncrementalHydration(),
        ),
        provideHttpClient(),
        provideTranslateService({
            // No `fallbackLang`: it would make ngx-translate load that language's file (`de.json`) on
            // top of the active one, so every non-`de` page issued a second, redundant request. The
            // `check-translations` CI enforces `en.json`/`de.json` key parity, so there is nothing to
            // fall back to. `provideAppLanguage` always sets a supported language before first paint.
            // Browser loads translations over HTTP; SSR uses a filesystem loader (app.config.server.ts).
            // The `?v=<BUILD_VERSION>` suffix cache-busts translations per build: the URL changes only
            // when a new build ships, so the browser/CDN can cache each version long-term (see the
            // immutable `Cache-Control` in src/server.ts) yet pick up new translations on deploy without
            // a hard reload.
            loader: provideTranslateHttpLoader({
                prefix: '/i18n/',
                suffix: `.json?v=${BUILD_VERSION}`,
            }),
        }),
        // Resolves the active language: saved preference → URL prefix → browser → `de`.
        provideAppLanguage(),
    ],
};
